import { useEffect, useState } from 'react'
import type { CustomerOrder, CustomerOrderInvoice } from '../customerOrdersApi'
import {
  applyOrderDetailError,
  fetchOrderDetailBundle,
  resetOrderDetailState,
} from './useCustomerOrders.detail'

type UseOrderDetailBundleInput = {
  authenticated: boolean
  token: string | null
  isOrderDetailRoute: boolean
  orderDetailId: string
}

type UseOrderDetailBundleResult = {
  orderDetail: CustomerOrder | null
  orderDetailLoading: boolean
  orderDetailError: string
  orderInvoices: CustomerOrderInvoice[]
  orderInvoicesLoading: boolean
  orderInvoicesError: string
}

export const useOrderDetailBundle = ({
  authenticated,
  token,
  isOrderDetailRoute,
  orderDetailId,
}: UseOrderDetailBundleInput): UseOrderDetailBundleResult => {
  const [orderDetail, setOrderDetail] = useState<CustomerOrder | null>(null)
  const [orderDetailLoading, setOrderDetailLoading] = useState(false)
  const [orderDetailError, setOrderDetailError] = useState('')
  const [orderInvoices, setOrderInvoices] = useState<CustomerOrderInvoice[]>([])
  const [orderInvoicesLoading, setOrderInvoicesLoading] = useState(false)
  const [orderInvoicesError, setOrderInvoicesError] = useState('')

  useEffect(() => {
    if (!authenticated || !isOrderDetailRoute || !orderDetailId) {
      return
    }

    let active = true
    const detailSetters = {
      setOrderDetail,
      setOrderDetailError,
      setOrderInvoices,
      setOrderInvoicesError,
    }

    void (async () => {
      setOrderDetailLoading(true)
      resetOrderDetailState(detailSetters)
      setOrderInvoicesLoading(true)
      try {
        const { order: nextOrder, invoices: nextInvoices } = await fetchOrderDetailBundle({
          token,
          orderDetailId,
        })
        if (!active) return
        setOrderDetail(nextOrder)
        setOrderInvoices(nextInvoices)
      } catch (nextError) {
        if (!active) return
        applyOrderDetailError(detailSetters, nextError)
      } finally {
        if (active) {
          setOrderDetailLoading(false)
          setOrderInvoicesLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [authenticated, isOrderDetailRoute, orderDetailId, token])

  return {
    orderDetail,
    orderDetailLoading,
    orderDetailError,
    orderInvoices,
    orderInvoicesLoading,
    orderInvoicesError,
  }
}
